import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SellIcon from "@mui/icons-material/TrendingDown";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCurrency } from "../Auth/AuthContext";

const PositionCard = ({
  position,
  gainLoss,
  isExpanded,
  onExpand,
  onSell,
  onAddMore,
  formatCurrency,
  formatQuantity,
}) => {
  const {
    selectedCurrency,
    convert,
    formatMoney,
    ratesReady,
    userCurrency,
    convertAndFormat,
  } = useCurrency();
  const navigate = useNavigate();
  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2, py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box flex={1}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography
                variant="h6"
                fontWeight={600}
                fontSize="1.1rem"
                lineHeight={1.2}
                onClick={() => navigate(`/stocks/${position.ticker_symbol}`)}
                sx={{ cursor: "pointer", "&:hover": { color: "primary.main" } }}
              >
                {position.ticker_symbol}
              </Typography>
              {position.company_name && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  lineHeight={1.2}
                >
                  - {position.company_name}
                </Typography>
              )}
              {position.exchange && (
                <Chip
                  label={position.exchange}
                  size="small"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
              {gainLoss &&
                gainLoss.priceChange != null &&
                gainLoss.priceChangePercent != null && (
                  <Chip
                    label={`${gainLoss.priceChange >= 0 ? "+" : ""}${
                      selectedCurrency === "DEFAULT"
                        ? convert(
                            gainLoss.priceChange,
                            position.currency,
                            position.currency,
                          ).toFixed(2)
                        : convert(
                            gainLoss.priceChange,
                            position.currency,
                            selectedCurrency,
                          ).toFixed(2)
                    } (${gainLoss.priceChangePercent >= 0 ? "+" : ""}${gainLoss.priceChangePercent.toFixed(2)}%)`}
                    size="small"
                    color={gainLoss.priceChange >= 0 ? "success" : "error"}
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
            </Box>

            {/* Stats Grid */}
            <Grid container spacing={1} sx={{ mt: 0 }}>
              <Grid item xs={6} sm={4} md={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                  lineHeight={1.1}
                >
                  Quantity
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  lineHeight={1.3}
                  sx={{ mt: 0.25 }}
                >
                  {formatQuantity(position.net_quantity)}
                </Typography>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                  lineHeight={1.1}
                >
                  Avg. Price
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={600}
                  lineHeight={1.3}
                  sx={{ mt: 0.25 }}
                >
                  {convertAndFormat(position.avg_price, position.currency)}
                </Typography>
              </Grid>

              {gainLoss && (
                <>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                      lineHeight={1.1}
                    >
                      Current Price
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="primary"
                      lineHeight={1.3}
                      sx={{ mt: 0.25 }}
                    >
                      {convertAndFormat(
                        gainLoss.currentPrice,
                        position.currency,
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={4} md={2}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                      lineHeight={1.1}
                    >
                      Total Invested
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      lineHeight={1.3}
                      sx={{ mt: 0.25 }}
                    >
                      {convertAndFormat(position.total_cost, position.currency)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={4} md={2}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                      lineHeight={1.1}
                    >
                      Current Value
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color="primary"
                      lineHeight={1.3}
                      sx={{ mt: 0.25 }}
                    >
                      {convertAndFormat(
                        gainLoss.currentValue,
                        position.currency,
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={4} md={2}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontSize="0.7rem"
                      lineHeight={1.1}
                    >
                      Gain/Loss
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      lineHeight={1.3}
                      sx={{ mt: 0.25 }}
                      color={
                        gainLoss.gainLoss >= 0 ? "success.main" : "error.main"
                      }
                    >
                      {gainLoss.gainLoss >= 0 ? "+" : ""}
                      {convertAndFormat(gainLoss.gainLoss, position.currency)}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontSize="0.7rem"
                      lineHeight={1.1}
                      color={
                        gainLoss.gainLossPercent >= 0
                          ? "success.main"
                          : "error.main"
                      }
                    >
                      ({gainLoss.gainLossPercent >= 0 ? "+" : ""}
                      {gainLoss.gainLossPercent.toFixed(2)}%)
                    </Typography>
                  </Grid>
                </>
              )}

              <Grid item xs={6} sm={4} md={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                  lineHeight={1.1}
                >
                  Broker
                </Typography>
                <Typography variant="body1" lineHeight={1.3} sx={{ mt: 0.25 }}>
                  {position.broker_name}
                </Typography>
              </Grid>
            </Grid>

            {/* Loading message */}
            {!gainLoss && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.5}
                fontSize="0.7rem"
                lineHeight={1.1}
              >
                📊 Loading prices...
              </Typography>
            )}

            {/* 52-Week Range */}
            {gainLoss?.fiftyTwoWeekHigh && gainLoss?.fiftyTwoWeekLow && (
              <Box mt={0.75} pt={0.75} borderTop={1} borderColor="divider">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontSize="0.7rem"
                  lineHeight={1.1}
                >
                  52-Week:{" "}
                  {/* {formatCurrency(gainLoss.fiftyTwoWeekLow, position.currency)}{" "} */}
                  {convertAndFormat(
                    gainLoss.fiftyTwoWeekLow,
                    position.currency,
                  )}{" "}
                  -{" "}
                  {convertAndFormat(
                    gainLoss.fiftyTwoWeekHigh,
                    position.currency,
                  )}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Box
                    flex={1}
                    sx={{
                      height: 4,
                      bgcolor: "grey.200",
                      borderRadius: 1,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        height: "100%",
                        background:
                          "linear-gradient(90deg, #f44336 0%, #ff9800 50%, #4caf50 100%)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        left: `${gainLoss.fiftyTwoWeekPosition}%`,
                        top: -2,
                        width: 3,
                        height: 8,
                        bgcolor: "primary.main",
                        borderRadius: 1,
                        boxShadow: 1,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="primary"
                    fontSize="0.7rem"
                    lineHeight={1.1}
                  >
                    {gainLoss.fiftyTwoWeekPosition.toFixed(0)}%
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Actions */}
          <Box display="flex" gap={0.5} ml={2}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<SellIcon />}
              onClick={onSell}
              sx={{ py: 0.5, px: 1, fontSize: "0.75rem" }}
            >
              Sell
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddMore}
              sx={{ py: 0.5, px: 1, fontSize: "0.75rem" }}
            >
              Add
            </Button>
            <IconButton size="small" onClick={onExpand}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PositionCard;
